import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { auth } from "@/lib/api";

import { log } from "@/lib/log";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Lazy init: if we're returning from an OAuth callback, AuthCallback will
  // exchange the session_id itself — skip the /me check and start un-loaded.
  const [loading, setLoading] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.location.hash?.includes("session_id=");
  });

  const checkAuth = useCallback(async () => {
    try {
      const u = await auth.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip the /me probe when AuthCallback is about to claim the session.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signup = useCallback(async (body) => {
    const u = await auth.signup(body);
    setUser(u);
    return u;
  }, []);
  const login = useCallback(async (body) => {
    const u = await auth.login(body);
    setUser(u);
    return u;
  }, []);
  const logout = useCallback(async () => {
    await auth.logout();
    setUser(null);
    // Clear any potentially-sensitive client-side state on logout. UI prefs
    // (theme, currency, recent searches, paywall counters) are intentionally
    // kept so the next user lands with a familiar experience — but tokens
    // and the admin API key must be wiped.
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("riskpedia_admin_api_key");
        localStorage.removeItem("rp_india_unlock");
        localStorage.removeItem("rp_india_unlock_email");
      } catch (err) {
        log.warn("localStorage cleanup failed on logout:", err);
      }
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, signup, login, logout, refresh: checkAuth, setUser }),
    [user, loading, signup, login, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
